import { Crown, Trophy, XCircle } from 'lucide-react'
import BracketPlayerNames from './BracketPlayerNames'
import { cn } from '../../lib/utils'

/**
 * Read-only Finals card for pool-play results — owner-approved "Option C"
 * (refined 2026-06-09). Two stacked mini-cards so each of the four questions
 * reads on its own instead of being inferred from a single mixed list:
 *
 *   ACTUAL FINAL    — the two real finalists on plain white rows; the official
 *                     winner is bold + a green Trophy. (matchup / who won)
 *   YOUR PREDICTION — your two predicted finalists; your champion pick wears a
 *                     Crown. (what you predicted / who you backed)
 *
 * Styling rules:
 *   - A pick that didn't pan out (a finalist who never reached the final, or a
 *     champion pick who lost) is struck through with a circled ✗; the Crown on
 *     a wrong champion pick is red too, so strikethrough / Crown / ✗ share one
 *     consistent red.
 *   - Picking the actual champion correctly is the only thing that turns a row
 *     green: green background + green Crown + green Trophy (no checkmark). This
 *     keeps "green" meaning exactly one thing — your correct champ pick.
 *
 * Two states, driven by whether an official winner is posted (DUB-186):
 *   - Matchups set, not yet scored: the actual finalists show with no winner
 *     highlight, and a predicted finalist who didn't reach the final is already
 *     marked wrong (✗ / strikethrough — that comparison is decided). The
 *     champion pick wears a neutral purple Crown ("your pick", verdict pending).
 *   - Scored: the winner gets the green Trophy and the champion verdict resolves
 *     to green (correct) or red (lost).
 *
 * Used once the official Finals matchups are set; the editable / pre-matchup
 * view keeps using <PoolMatchCard />.
 */
const RED = 'text-[var(--color-pick-wrong-text)]'
const GREEN = 'text-[var(--color-pick-correct-text)]'
const PRIMARY = 'text-[var(--color-primary)]'

export default function SundayFinalsResultCard({
  userTeamA,
  userTeamB,
  officialTeamA,
  officialTeamB,
  userWinnerPickId,
  officialWinnerId,
  teamSize = 1,
}) {
  const officialFinalists = [officialTeamA, officialTeamB].filter(Boolean)
  const officialIds = new Set(officialFinalists.map(t => t.id))
  const userFinalists = [userTeamA, userTeamB].filter(Boolean)
  const resultPosted = !!officialWinnerId

  return (
    <div className="space-y-3">
      {/* ---- ACTUAL FINAL ---- */}
      <div>
        <div className={cn(
          'mb-1 text-[11px] font-semibold uppercase tracking-wide',
          resultPosted ? 'text-[var(--color-pick-correct-text)]' : 'text-muted-foreground',
        )}>
          {resultPosted ? 'Actual final' : 'Actual matchup'}
        </div>
        <div className="rounded-md border bg-background overflow-hidden transition-all duration-200 ease-out hover:-translate-y-1 hover:shadow-lg">
          {officialFinalists.map((team, i) => {
            const isWinner = !!officialWinnerId && team.id === officialWinnerId
            return (
              <div
                key={team.id}
                className={cn(
                  'flex items-center gap-2 px-2 py-2 w-full',
                  i < officialFinalists.length - 1 && 'border-b',
                )}
              >
                <div className="flex-1 min-w-0">
                  <BracketPlayerNames
                    team={team}
                    teamSize={teamSize}
                    className={cn(isWinner && 'font-semibold')}
                  />
                </div>
                {isWinner && <Trophy className={cn('h-4 w-4 shrink-0', GREEN)} />}
              </div>
            )
          })}
        </div>
      </div>

      {/* ---- YOUR PREDICTION ---- */}
      {/* Faint purple wash (echoes the purple label) chunks this whole zone as
          "your stuff", distinct from the Actual Final block above. Kept light so
          the green correct-champ row highlight still reads on top of it. */}
      <div className="rounded-lg bg-[var(--color-primary)]/5 p-2">
        <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[var(--color-primary)]">
          Your prediction
        </div>
        <div className="rounded-md border bg-background overflow-hidden transition-all duration-200 ease-out hover:-translate-y-1 hover:shadow-lg">
          {userFinalists.map((team, i) => {
            const isOfficialFinalist = officialIds.has(team.id)
            const isOfficialWinner = !!officialWinnerId && team.id === officialWinnerId
            const isUserWinnerPick = !!userWinnerPickId && team.id === userWinnerPickId
            const correctChamp = isUserWinnerPick && isOfficialWinner
            // A predicted finalist who isn't in the actual final is wrong the
            // moment the matchups are set. A champion pick who lost is only wrong
            // once the match is scored — until then their Crown stays neutral.
            const missedFinal = !isOfficialFinalist
            const championLost = resultPosted && isUserWinnerPick && !isOfficialWinner
            const struck = missedFinal || championLost
            const crownColor = struck ? RED : correctChamp ? GREEN : PRIMARY
            return (
              <div
                key={team.id}
                className={cn(
                  'flex items-center gap-2 px-2 py-2 w-full',
                  i < userFinalists.length - 1 && 'border-b',
                  correctChamp && 'bg-emerald-500/15',
                )}
              >
                <div className="flex-1 min-w-0">
                  <BracketPlayerNames
                    team={team}
                    teamSize={teamSize}
                    className={cn(
                      struck && cn('line-through', RED),
                      correctChamp && 'font-semibold',
                    )}
                  />
                </div>
                {/* Champion pick → Crown. Red when wrong (matches the strike +
                    ✗); green when you called the champion (matches the Trophy);
                    neutral purple while the match is still unscored. */}
                {isUserWinnerPick && (
                  <Crown className={cn('h-4 w-4 shrink-0', crownColor)} />
                )}
                {/* Correct champ also gets the green Trophy (parallels the
                    Actual Final winner) — no checkmark. */}
                {correctChamp && <Trophy className={cn('h-4 w-4 shrink-0', GREEN)} />}
                {struck && <XCircle className={cn('h-4 w-4 shrink-0', RED)} />}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
